/**
 * Arreglo 1.5 (oct-2026) — Mis clientes (estilo Booksy): lista alfabética paginada para miles de clientes,
 * búsqueda nombre/apellido/teléfono/correo, índice A–Z, alta manual e importación CSV.
 * Fuente: `barber_clients` (solo los del barbero dueño).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, UserPlus, Upload, Ban, ChevronRight, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { clientsApi } from '@/lib/clientsApi';
import { ClientSheet } from './ClientSheet';
import { ClientEditSheet } from './ClientEditSheet';
import { ClientImportSheet } from './ClientImportSheet';
import { NameDuplicatesCard } from './NameDuplicatesCard';
import { tr, localeTag } from '@/contexts/LanguageContext';

const PAGE = 100;

export const ClientInitial = ({ client, size = 'w-10 h-10 text-sm' }) => (
  client?.avatar || client?.avatar_url
    ? <img src={client.avatar || client.avatar_url} alt="" className={`${size} rounded-full object-cover border border-border flex-shrink-0`} />
    : <span className={`${size} rounded-full bg-primary/15 text-primary font-display font-bold flex items-center justify-center flex-shrink-0`}>{client?.initial || '#'}</span>
);

export const ClientsPanel = ({ onBookFor, onSellFor }) => {
  const [q, setQ] = useState('');
  const [letter, setLetter] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, grand_total: 0, letters: [] });
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const sentinel = useRef(null);

  const load = useCallback(async (offset = 0) => {
    offset ? setMore(true) : setLoading(true);
    try {
      const r = await clientsApi.list({ q, letter, limit: PAGE, offset });
      setData((prev) => offset ? { ...r, items: [...prev.items, ...(r.items || [])] } : r);
    } catch { if (!offset) setData({ items: [], total: 0, grand_total: 0, letters: [] }); }
    finally { setLoading(false); setMore(false); }
  }, [q, letter]);

  useEffect(() => { const t = setTimeout(() => load(0), q ? 280 : 0); return () => clearTimeout(t); }, [load, q]);

  const hasMore = data.items.length < (data.total || 0);
  useEffect(() => {
    if (!hasMore || !sentinel.current) return undefined;
    const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting && !more) load(data.items.length); }, { rootMargin: '200px' });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [hasMore, more, data.items.length, load]);

  const letters = data.letters || [];
  let lastInitial = null;

  return (
    <div className="relative max-w-xl mx-auto" data-testid="clients-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs text-muted-foreground" data-testid="clients-total">
          <b className="text-foreground">{Number(data.grand_total || 0).toLocaleString(localeTag())}</b> {tr("clientes")}{(q || letter) ? ` ${tr("· {0} en esta vista", data.total)}` : ''}
        </p>
        <div className="flex gap-1.5">
          <button type="button" onClick={() => setImporting(true)} className="h-9 px-3 rounded-full border border-border text-xs font-semibold inline-flex items-center gap-1.5 hover:border-primary/50" data-testid="clients-import-btn"><Upload className="w-3.5 h-3.5" /> CSV</button>
          <button type="button" onClick={() => setCreating(true)} className="h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-bold inline-flex items-center gap-1.5" data-testid="clients-new-btn"><UserPlus className="w-3.5 h-3.5" />{' '}{tr("Nuevo")}</button>
        </div>
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => { setQ(e.target.value); setLetter(null); }} placeholder={tr("Nombre, apellido, teléfono o correo")} className="pl-9 bg-card h-11" data-testid="clients-search-input" />
      </div>
      {!q && !letter && <div className="mb-2"><NameDuplicatesCard compact onChanged={() => load(0)} /></div>}
      {letter && (
        <button type="button" onClick={() => setLetter(null)} className="mb-2 text-xs text-primary font-semibold" data-testid="clients-letter-clear">{tr("Letra «{0}» · ver todos", letter)}</button>
      )}

      <div className="flex gap-2">
        <ul className="flex-1 min-w-0 divide-y divide-border/60 bg-card border border-border rounded-2xl overflow-hidden" data-testid="clients-list">
          {loading && <li className="p-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{' '}{tr("Cargando clientes…")}</li>}
          {!loading && data.items.length === 0 && (
            <li className="p-8 text-center text-sm text-muted-foreground" data-testid="clients-empty">{q ? tr("Sin resultados para esa búsqueda.") : tr("Aún no tienes clientes. Cada cita los agrega aquí; también puedes importarlos por CSV.")}</li>
          )}
          {!loading && data.items.map((c) => {
            const showHeader = !q && c.initial !== lastInitial;
            lastInitial = c.initial;
            return (
              <li key={c.id} id={`clients-letter-${c.initial}`}>
                {showHeader && <p className="px-3 pt-2 pb-0.5 text-[10px] font-bold text-primary tracking-widest bg-background/40">{c.initial}</p>}
                <button type="button" onClick={() => setOpenId(c.id)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-secondary/50" data-testid={`clients-row-${c.id}`}>
                  <ClientInitial client={c} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold truncate">{c.display}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">{[c.phone_display || c.phone, c.email].filter(Boolean).join(' · ') || tr("Sin contacto")}</span>
                  </span>
                  {c.online_booking_blocked && <Ban className="w-4 h-4 text-destructive flex-shrink-0" aria-label={tr("Reserva online bloqueada")} data-testid={`clients-row-${c.id}-blocked`} />}
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </button>
              </li>
            );
          })}
          {hasMore && <li ref={sentinel} className="p-3 text-center text-xs text-muted-foreground">{more ? tr("Cargando…") : tr("{0} más", data.total - data.items.length)}</li>}
        </ul>
        {letters.length > 1 && !q && (
          <nav className="flex flex-col items-center gap-[1px] text-[10px] font-bold text-primary select-none sticky top-2 self-start" aria-label={tr("Índice alfabético")} data-testid="clients-az-index">
            {letters.map((l) => (
              <button key={l} type="button" onClick={() => setLetter(l === letter ? null : l)} className={`w-5 h-4 leading-4 rounded ${letter === l ? 'bg-primary text-primary-foreground' : ''}`} data-testid={`clients-az-${l}`}>{l}</button>
            ))}
          </nav>
        )}
      </div>

      {openId && (
        <ClientSheet clientId={openId} onClose={() => setOpenId(null)} onChanged={() => load(0)}
          onBook={(c) => { setOpenId(null); onBookFor?.(c); }} onSell={(c) => { setOpenId(null); onSellFor?.(c); }} />
      )}
      {creating && <ClientEditSheet open mode="create" onOpenChange={(o) => !o && setCreating(false)} onSaved={() => { setCreating(false); load(0); }} />}
      {importing && <ClientImportSheet open onOpenChange={(o) => !o && setImporting(false)} onImported={() => load(0)} />}
    </div>
  );
};

export default ClientsPanel;
